import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { infoCircleIcon, moreVerticalIcon } from '@progress/kendo-svg-icons';
import { AutomatedResponseUsageData } from 'src/app/core/models/analytics/soc-models';
import { AlertDataDialogComponent } from 'src/app/core/components/alert-data-dialog';
import { AlertDataDialogConfig, ContextMenuEvent } from 'src/app/core/models/dialog-config';
import { QueryType } from 'src/app/core/services/analytics/soc-analytics.service';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';
import { environment } from 'src/environments/environment';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';

@Component({
  standalone: true,
  selector: 'app-automated-response-usage',
  imports: [CommonModule, DropDownButtonComponent, IconsModule, AlertDataDialogComponent, TooltipModule, NotificationModule],
  templateUrl: './automated-response-usage.component.html',
  styleUrl: './automated-response-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomatedResponseUsageComponent extends BaseComponent {
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;

  public alertDialogConfig = signal<AlertDataDialogConfig | null>(null);
  public showAlertDialog = signal(false);
  automatedResponseData = input.required<AutomatedResponseUsageData[]>();
  dateRange = input<string[] | null>(null);
  organizationId = input<number | null>();
  startDate = input<string | null>();
  endDate = input<string | null>();
  queryType = input<QueryType>(QueryType.All);
  selectedOrgIds = input<number[]>([]);

  /** Optional array of column field names to display in alert dialogs. If not provided, all columns are shown. */
  alertVisibleColumns = input<string[]>();

  /** Optional security tool filter - comma-separated list of product IDs */
  securityTool = input<string>();

  /** Whether the alert dialog shows the Export to Excel button. Defaults to true. */
  showExcelExport = input<boolean>(true);

  /** Injected services for server-side CSV export via report-job pipeline */
  private http = inject(HttpClient);
  private reportJobService = inject(ReportJobService);
  private notificationService = inject(NotificationService);

  /** Whether the Download CSV button is offered (opt-in per context; off by default). */
  enableCsvExport = input(false);

  /** Whether a CSV export is currently being queued */
  public isExporting = signal(false);

  /** Resolved report template id for this widget's report type */
  private reportTemplateId = signal<number | null>(null);

  /** SOC report type key for this widget (intrinsic to the widget). */
  private readonly reportKey = 'automated-response-usage-soc-report';

  // Computed properties for formatting and calculations
  formattedData = computed(() => {
    const data = this.automatedResponseData();
    const maxCount = Math.max(...data.map(item => item.count));

    return data.map(item => ({
      ...item,
      formattedCount: this.formatCount(item.count),
      barWidth: (item.count / maxCount) * 100,
      priority: this.getPriority(item.count, maxCount)
    }));
  });

  // Computed total count for summary
  totalUsages = computed(() => {
    return this.automatedResponseData().reduce((sum, item) => sum + item.count, 0);
  });

  constructor() {
    super();
    this.loadReportTemplateId();
  }

   /**
 * Handles context menu selection for viewing data
 */
  onContextMenuSelect(event: ContextMenuEvent): void {
    if (!event?.text) {
      console.warn('Invalid event structure:', event);
      return;
    }

    if (event.text === 'View Alert Data') {
      this.openAlertDialog();
    }
  }

  /**
   * Opens the alert data dialog with configuration
   */
  private openAlertDialog(): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const config: AlertDataDialogConfig = {
      organizationId: this.organizationId() ?? null,
      startDate: this.startDate() ?? null,
      endDate: this.endDate() ?? null,
      queryType: this.queryType(),
      selectedOrgIds: this.selectedOrgIds(),
      event: 'raw_events',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`,
      visibleColumns: this.alertVisibleColumns(),
      securityTool: this.securityTool(),
      showExcelExport: this.showExcelExport(),
      reportTypeKey: this.enableCsvExport() ? this.reportKey : undefined,
      additionalParams: { customer_tags_only: 'true' },
    };

    this.alertDialogConfig.set(config);
    this.showAlertDialog.set(true);
  }

  /**
   * Handles alert dialog close
   */
  onAlertDialogClose(): void {
    this.showAlertDialog.set(false);
    this.alertDialogConfig.set(null);
  }

  /**
   * Resolve the report template id for this widget's report type key.
   */
  private loadReportTemplateId(): void {
    const url = `${environment.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${this.reportKey}'`;
    this.http
      .get<{ value: any[] }>(url)
      .pipe(map((response) => response.value?.[0]?.report_template?.id ?? null))
      .subscribe({
        next: (id) => this.reportTemplateId.set(id),
        error: (err) => console.error('Failed to load report template:', err),
      });
  }

  /**
   * Queue a server-side CSV export for this widget's report type.
   */
  public triggerExport(): void {
    const templateId = this.reportTemplateId();
    const orgId = this.organizationId();
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!templateId || !orgId || !startDate || !endDate) {
      this.notificationService.show({
        content: 'Unable to export: missing organization or date range.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        hideAfter: 5000,
      });
      return;
    }

    this.isExporting.set(true);

    const config: ReportJobConfig = {
      organizations: [orgId],
      primary_organization_id: orgId,
      report_template_id: templateId,
      start_date: formatDate(startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      end_date: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      delivery_method: 'download',
      report_params: {
        filter_type: this.queryType(),
        ...(this.securityTool() ? { security_tool: this.securityTool()! } : {}),
      },
    };

    this.reportJobService.createReportJob(config).subscribe({
      next: () => {
        this.isExporting.set(false);
        this.notificationService.show({
          content: 'Export queued. You will find the CSV in the Reports history once generated.',
          type: { style: 'success', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          closable: true,
          hideAfter: 5000,
        });
      },
      error: () => {
        this.isExporting.set(false);
        this.notificationService.show({
          content: 'Failed to queue export. Please try again.',
          type: { style: 'error', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          hideAfter: 5000,
        });
      },
    });
  }

  private formatCount(count: number): string {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  }

  private getPriority(count: number, maxCount: number): string {
    const percentage = (count / maxCount) * 100;
    if (percentage >= 75) return 'high';
    if (percentage >= 50) return 'medium';
    if (percentage >= 25) return 'low';
    return 'minimal';
  }
}
